/**
 * Smart Plan Expired Modal Component
 *
 * Non-dismissible modal that appears when a Smart plan user's subscription expires.
 * Informs the user their subscription has ended and they will be downgraded to Free plan.
 *
 * @module @ideoz/ui/components/molecules/SmartPlanExpiredModal
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../atoms/dialog';
import { Button } from '../atoms/button';
import { InfoSection } from '../molecules/info-section';
import { cn } from '@ideoz/ui';
import type { SmartPlanExpiredModalProps, DowngradeConfirmationDialogProps } from '../../types/SmartPlanExpiredModal.types';

/**
 * DowngradeConfirmationDialog Component
 *
 * Confirmation dialog that appears when user clicks "Proceed to downgrade"
 */
export const DowngradeConfirmationDialogComponent: React.FC<DowngradeConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  planName,
  isProcessing = false,
  className = '',
  testId = 'downgrade-confirmation-dialog',
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && onClose()}>
      <DialogContent
        className={cn(
          className
        )}
        data-testid={testId}
        onInteractOutside={(e) => {
          // Prevent closing on backdrop click when processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing on Escape when processing
          if (isProcessing) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle >
            Confirm Downgrade
          </DialogTitle>
        </DialogHeader>

        <DialogDescription >
          Are you sure you want to proceed with downgrading to the Free plan? You will lose access to all {planName} plan features.
        </DialogDescription>

        <DialogFooter >
          <Button
            variant="secondary-gray"
            onClick={onClose}
            disabled={isProcessing}
            data-testid={`${testId}-cancel-button`}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isProcessing}
            data-testid={`${testId}-confirm-button`}
          >
            {isProcessing ? 'Processing...' : 'Confirm Downgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * SmartPlanExpiredModal Component
 *
 * Non-dismissible modal that appears when subscription expires.
 * Blocks all app interactions until user chooses to renew or downgrade.
 *
 * @component
 * @example
 * ```tsx
 * <SmartPlanExpiredModal
 *   isOpen={true}
 *   planName="Smart"
 *   onRenewSubscription={() => handleRenew()}
 *   onProceedToDowngrade={() => handleDowngrade()}
 * />
 * ```
 */
export const SmartPlanExpiredModal: React.FC<SmartPlanExpiredModalProps> = ({
  isOpen,
  planName,
  onRenewSubscription,
  onProceedToDowngrade,
  isProcessing = false,
  className = '',
  testId = 'smart-plan-expired-modal',
}) => {
  // Handle downgrade button click - calls onProceedToDowngrade directly
  const handleProceedToDowngrade = () => {
    if (onProceedToDowngrade) {
      onProceedToDowngrade();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className={cn('sm:max-w-md', className)}
        data-testid={testId}
        onInteractOutside={(e) => {
          // Prevent closing on backdrop click - modal is non-dismissible
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          // Prevent closing on Escape - modal is non-dismissible
          e.preventDefault();
        }}
      >
        {/* Header - No close button (non-dismissible) */}
        <DialogHeader>
          <DialogTitle>
            Downgrade Subscription
          </DialogTitle>
        </DialogHeader>

        {/* Description */}
        <DialogDescription>
          Your subscription ended and you will be downgraded to free plan.
        </DialogDescription>

        {/* What will happen next? Section */}
        <div className="mt-2 space-y-3">
          <h3 className="text-sm font-semibold">
            What will happen next?
          </h3>
          <ul className="space-y-2 list-disc list-inside text-sm ml-2">
            <li>Your subscription will be cancelled immediately</li>
            <li>You'll lose access to "{planName}" features</li>
          </ul>
        </div>

        {/* Changed your mind? Info Box */}
        <div className="mt-2">
          <InfoSection
            title="Changed your mind?"
            description={`You can renew your subscription now and continue using the benefits of ${planName}.`}
            variant="info"
            data-testid={`${testId}-info-box`}
          />
        </div>

        {/* Action Buttons */}
        <DialogFooter>
          <Button
            onClick={onRenewSubscription}
            disabled={!onRenewSubscription || isProcessing}
            variant="secondary-gray"
            data-testid={`${testId}-renew-button`}
          >
            Renew subscription
          </Button>
          <Button
            onClick={handleProceedToDowngrade}
            disabled={isProcessing}
            variant="destructive"
            data-testid={`${testId}-downgrade-button`}
          >
            {isProcessing ? 'Processing...' : 'Proceed to downgrade'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

SmartPlanExpiredModal.displayName = 'SmartPlanExpiredModal';

export default SmartPlanExpiredModal;
